// Search-only input transport. No results or native capabilities cross this API.
import { acceptedMobileNavigation } from "../mobile_webview_navigation.mjs"

const integer = (value) => Number.isSafeInteger(value) && value > 0
const exact = (value, keys) =>
  value &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  Object.keys(value).sort().join(",") === keys.sort().join(",")

const prepared = new WeakMap()

// Install before LiveSocket.connect. The small hydration window must not allow
// LiveView to enqueue a server form debounce before the hook takes ownership.
export function prepareNativeSearch(win = window) {
  if (prepared.has(win)) return prepared.get(win)
  const state = {
    mounted: new WeakSet(),
    pending: new WeakSet(),
    composing: new WeakSet(),
  }
  prepared.set(win, state)
  const transport = (event) =>
    event.target.closest?.("#global-search-transport[data-mobile-app=true]")
  for (const name of ["input", "change", "submit"]) {
    win.addEventListener(
      name,
      (event) => {
        const el = transport(event)
        if (!el || state.mounted.has(el)) return
        event.preventDefault()
        event.stopImmediatePropagation()
        state.pending.add(el)
      },
      true,
    )
  }
  // Replaying mid-composition would commit candidate text, so mount defers the
  // replay to compositionend. Registered first, these run before the hook's.
  win.addEventListener(
    "compositionstart",
    (event) => {
      const el = transport(event)
      if (el) state.composing.add(el)
    },
    true,
  )
  win.addEventListener(
    "compositionend",
    (event) => {
      const el = transport(event)
      if (el) state.composing.delete(el)
    },
    true,
  )
  return state
}

export function validNativeSearchQuery(query) {
  return (
    typeof query === "string" &&
    !query.includes("\0") &&
    !Array.from(query).some((point) => {
      const code = point.codePointAt(0)
      return code >= 0xd800 && code <= 0xdfff
    }) &&
    new TextEncoder().encode(query).length <= 2048 &&
    [
      ...new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(
        query,
      ),
    ].length <= 200
  )
}

// Validate raw key spelling before decoding values, just like SearchNavigation.
export function searchRootQuery(location) {
  if (location.pathname !== "/search" || location.hash) return null
  if (!location.search || location.search === "?") return ""
  const match = /^\?search_query=([^&]*)$/.exec(location.search)
  if (!match) return null
  try {
    const query = decodeURIComponent(match[1].replace(/\+/g, " "))
    return validNativeSearchQuery(query) ? query : null
  } catch {
    return null
  }
}

// Same test as LiveView's wantsNewTab: these clicks leave this tab on Search,
// so no phx:navigate would ever settle a retirement they started.
function opensElsewhere(event, link) {
  const target = link.getAttribute("target")
  return (
    event.ctrlKey ||
    event.shiftKey ||
    event.metaKey ||
    event.button === 1 ||
    link.hasAttribute("download") ||
    (target !== null &&
      (target.toLowerCase() === "_blank" || !target.startsWith("_")))
  )
}

export function installNativeSearch(hook, win = window) {
  const document = win.crypto.randomUUID()
  let destroyed = false,
    navigating = false
  let htmlTimer = null,
    htmlQuery = null,
    htmlInFlight = null
  let activation = null,
    highestActivation = 0,
    receivedSequence = 0,
    appliedSequence = 0
  let active = false,
    retired = false,
    pending = null,
    inFlight = null,
    receivedQuery = null
  const connected = () => hook.liveSocket.isConnected()
  const root = () =>
    !destroyed &&
    !navigating &&
    searchRootQuery(win.location) !== null &&
    hook.el.dataset.root === "true"
  const committedQuery = () => hook.el.dataset.query || ""
  const form = () => hook.el.querySelector("form")
  const fallback = () => {
    const element = form()
    if (!element) return
    const header = hook.el
      .closest("#global-search-section")
      ?.querySelector('[data-section-region="header"]')
    // Public JS visibility is sticky across LiveView patches. Hide the wrapper
    // too so a native-owned input leaves no empty flex item or duplicate title.
    for (const el of [header, hook.el].filter(Boolean)) {
      if (active) hook.js().hide(el)
      else hook.js().show(el, { display: el === header ? "flex" : "block" })
    }
    element.hidden = active
    element.querySelectorAll("input, button").forEach((input) => {
      input.disabled = active
    })
  }
  const status = () => ({
    version: 1,
    document,
    state: active
      ? connected()
        ? "active"
        : "offline"
      : retired
        ? "retired"
        : connected() && root()
          ? "ready"
          : "offline",
    connected: connected(),
    root: root(),
    activation,
    receivedSequence,
    appliedSequence,
    committedQuery: committedQuery(),
  })
  const reply = (accepted, reason = null) => ({ ...status(), accepted, reason })
  const cancelHTML = () => {
    win.clearTimeout(htmlTimer)
    htmlTimer = null
    htmlQuery = null
  }
  const retire = () => {
    cancelHTML()
    htmlInFlight = null
    active = false
    retired = true
    pending = null
    inFlight = null
    fallback()
  }
  const patch = (query) =>
    hook
      .js()
      .patch(
        query
          ? `/search?${new URLSearchParams({ search_query: query })}`
          : "/search",
        { replace: true },
      )
  const flushHTML = () => {
    if (active || !root() || !connected() || htmlQuery === null) return
    if (htmlInFlight !== null || htmlTimer !== null) return
    const query = htmlQuery
    htmlQuery = null
    if (query !== committedQuery() || query !== searchRootQuery(win.location)) {
      htmlInFlight = query
      patch(query)
    }
  }
  const flush = () => {
    if (!active || !root() || !connected() || inFlight || !pending) return
    const next = pending
    pending = null
    if (
      next.query === committedQuery() &&
      searchRootQuery(win.location) === next.query
    ) {
      appliedSequence = next.sequence
      return
    }
    inFlight = next
    patch(next.query)
  }
  const api = Object.freeze({
    status,
    activate(payload) {
      if (
        !exact(payload, ["document", "activation"]) ||
        !integer(payload.activation)
      )
        return reply(false, "invalid")
      if (destroyed || payload.document !== document)
        return reply(false, "stale")
      if (!root() || htmlInFlight !== null) return reply(false, "unavailable")
      if (active && payload.activation === activation) return reply(true)
      if (active) return reply(false, "unavailable")
      if (payload.activation <= highestActivation) return reply(false, "stale")
      if (!root() || !connected()) return reply(false, "unavailable")
      // Typed text still in its debounce reaches the server before takeover;
      // discarding it would drop what the user just typed. Native polls again.
      if (htmlQuery !== null) {
        win.clearTimeout(htmlTimer)
        htmlTimer = null
        flushHTML()
        return reply(false, "unavailable")
      }
      cancelHTML()
      activation = highestActivation = payload.activation
      receivedSequence = appliedSequence = 0
      receivedQuery = null
      active = true
      retired = false
      fallback()
      return reply(true)
    },
    submit(payload) {
      if (
        !exact(payload, ["document", "activation", "sequence", "query"]) ||
        !integer(payload.sequence) ||
        !validNativeSearchQuery(payload.query)
      )
        return reply(false, "invalid")
      if (
        destroyed ||
        payload.document !== document ||
        payload.activation !== activation ||
        !active
      )
        return reply(false, "stale")
      if (!root()) {
        retire()
        return reply(false, "unavailable")
      }
      if (
        payload.sequence === receivedSequence &&
        payload.query === receivedQuery
      )
        return reply(true)
      if (payload.sequence <= receivedSequence) return reply(false, "stale")
      receivedSequence = payload.sequence
      receivedQuery = payload.query
      pending = { sequence: payload.sequence, query: payload.query }
      flush()
      return reply(true)
    },
    retire(payload) {
      if (
        !exact(payload, ["document", "activation"]) ||
        !integer(payload.activation)
      )
        return reply(false, "invalid")
      if (
        destroyed ||
        payload.document !== document ||
        payload.activation !== activation
      )
        return reply(false, "stale")
      navigating = true
      retire()
      return reply(true)
    },
  })
  const update = () => {
    if (!root()) retire()
    if (
      htmlInFlight !== null &&
      committedQuery() === htmlInFlight &&
      searchRootQuery(win.location) === htmlInFlight
    ) {
      htmlInFlight = null
      flushHTML()
    }
    if (
      active &&
      inFlight &&
      committedQuery() === inFlight.query &&
      searchRootQuery(win.location) === inFlight.query
    ) {
      appliedSequence = inFlight.sequence
      inFlight = null
    }
    fallback()
    flush()
  }
  const intent = () => {
    navigating = true
    retire()
  }
  const settled = () => {
    navigating = false
    update()
  }
  const click = (event) => {
    const link = event.target.closest?.("a[href]")
    if (
      link &&
      !opensElsewhere(event, link) &&
      new URL(link.href, win.location.href).pathname !== "/search"
    )
      intent()
  }
  const navigate = (event) => {
    const navigation = acceptedMobileNavigation(event, hook.liveSocket)
    if (navigation && navigation.path !== "/search") intent()
  }
  const blockForm = (event) => {
    if (!form()?.contains(event.target)) return
    // Capture before LiveView queues its debounce. Both inputs then use the
    // client's link ordering; takeover cannot leave a server push_patch behind.
    event.preventDefault()
    event.stopImmediatePropagation()
    if (active || !root()) return
    const input = form().querySelector("input[name=search_query]")
    cancelHTML()
    // Candidate text is not a query yet; compositionend replays the result.
    if (event.isComposing) return
    const valid = validNativeSearchQuery(input.value)
    input.setCustomValidity(valid ? "" : hook.el.dataset.queryError)
    input.reportValidity()
    if (!valid) return
    htmlQuery = input.value
    if (event.type === "submit") flushHTML()
    else
      htmlTimer = win.setTimeout(() => {
        htmlTimer = null
        flushHTML()
      }, 300)
  }
  const replayHTML = (target) =>
    blockForm({
      target,
      type: "input",
      preventDefault() {},
      stopImmediatePropagation() {},
    })
  const compositionEnd = (event) => {
    if (form()?.contains(event.target)) replayHTML(event.target)
  }
  const hydration = prepareNativeSearch(win)
  hydration.mounted.add(hook.el)
  win.addEventListener("click", click, true)
  win.addEventListener("phx:before-navigate", intent)
  win.addEventListener("phx:navigate", settled)
  win.addEventListener("enaia:mobile:navigate:v1", navigate, true)
  for (const name of ["input", "change", "submit"])
    win.addEventListener(name, blockForm, true)
  win.addEventListener("compositionend", compositionEnd, true)
  win.enaiaSearchV1 = api
  // Input captured before mount; an open composition replays on compositionend.
  if (hydration.pending.has(hook.el) && !hydration.composing.has(hook.el))
    replayHTML(form())
  return {
    update,
    reconnect() {
      if (inFlight && !pending) pending = inFlight
      inFlight = null
      if (htmlInFlight !== null && htmlQuery === null) htmlQuery = htmlInFlight
      htmlInFlight = null
      update()
      flushHTML()
    },
    destroy() {
      destroyed = true
      retire()
      win.removeEventListener("click", click, true)
      win.removeEventListener("phx:before-navigate", intent)
      win.removeEventListener("phx:navigate", settled)
      win.removeEventListener("enaia:mobile:navigate:v1", navigate, true)
      for (const name of ["input", "change", "submit"])
        win.removeEventListener(name, blockForm, true)
      win.removeEventListener("compositionend", compositionEnd, true)
      if (win.enaiaSearchV1 === api) delete win.enaiaSearchV1
    },
  }
}

export const NativeSearch = {
  mounted() {
    const hydration = prepareNativeSearch()
    hydration.mounted.add(this.el)
    if (
      this.el.dataset.mobileApp !== "true" ||
      !globalThis.Intl?.Segmenter ||
      !globalThis.TextEncoder ||
      !globalThis.crypto?.randomUUID ||
      typeof this.js?.().patch !== "function"
    ) {
      const input = this.el.querySelector("input[name=search_query]")
      const replay = () =>
        input?.dispatchEvent(new Event("input", { bubbles: true }))
      if (!hydration.pending.has(this.el)) return
      if (hydration.composing.has(this.el))
        input?.addEventListener("compositionend", replay, { once: true })
      else replay()
      return
    }
    this.searchTransport = installNativeSearch(this)
  },
  updated() {
    this.searchTransport?.update()
  },
  reconnected() {
    this.searchTransport?.reconnect()
  },
  destroyed() {
    this.searchTransport?.destroy()
  },
}
